import { useCallback, useState } from 'react';
import type { SearchHit } from '@nexops/shared';
import { useCurrentUser } from '@/features/auth/auth.store';

const MAX_RECENT = 6;

/** Ostatnio otwarte wyniki — tylko na tym urządzeniu, osobno dla każdego użytkownika. Bez localStorage działa po cichu. */
export function useRecentSearches() {
  const userId = useCurrentUser()?.id ?? 'anon';
  const key = `nexops-recent-search:${userId}`;

  const read = useCallback((): SearchHit[] => {
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      return Array.isArray(parsed) ? (parsed as SearchHit[]).slice(0, MAX_RECENT) : [];
    } catch {
      return [];
    }
  }, [key]);

  const [recent, setRecent] = useState<SearchHit[]>(read);

  const push = useCallback(
    (hit: SearchHit) => {
      const next = [hit, ...read().filter((h) => !(h.type === hit.type && h.id === hit.id))].slice(0, MAX_RECENT);
      setRecent(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage unavailable — keep in memory only */
      }
    },
    [key, read],
  );

  const clear = useCallback(() => {
    setRecent([]);
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }, [key]);

  const refresh = useCallback(() => setRecent(read()), [read]);

  return { recent, push, clear, refresh };
}
